import formStyles from "./UserCreationForm.module.css"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import api from "../api"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-plus.svg"
import { ButtonStatus } from "../common/constants"
import { Each } from "../common/Each"
import useWindowDimensions from "../common/hooks/useWindowDimensions"
import { DialogStatus } from "../enums"
import { capitalize } from "../utils"
import StatusBadge from "./StatusBadge"
import Button from "./Button"
import Dialog from "./dialogs/Dialog"
import Pagination from "./Pagination"
import Select from "./Select"
import styles from "./Students.module.css"
import Table from "./Table"
import TextInput from "./TextInput"
import TooltipWrapper from "./TooltipWrapper"

const Students = forwardRef(({ orderBy, sortDirection }, ref) => {
    const { width } = useWindowDimensions()
    const { t } = useTranslation()

    const [loadingStudents, setLoadingStudents] = useState(false)
    const [loadingPage, setLoadingPage] = useState(true)
    const [students, setStudents] = useState([])
    const [count, setCount] = useState(null)
    const [page, setPage] = useState(0)
    const [per, setPer] = useState(25)
    const requestRef = useRef(null)
    const [q, setQ] = useState(null)

    const [addStatus, setAddStatus] = useState(null)
    const [open, setOpen] = useState(false)
    const [email, setEmail] = useState("")
    const [name, setName] = useState("")
    const [surname, setSurname] = useState("")
    const [courses, setCourses] = useState([])
    const [filteredCourses, setFilteredCourses] = useState([])
    const [editions, setEditions] = useState([])
    const [selectedCourse, setSelectedCourse] = useState(null)
    const [selectedEdition, setSelectedEdition] = useState(null)
    const [loading, setLoading] = useState({courses: false, editions: false})

    const getStudents = useCallback(async (q = null) => {
        requestRef.current?.abort()
        const controller = new AbortController()
        requestRef.current = controller

        setLoadingStudents(true)
        try {
            const params = new URLSearchParams()

            if (q) {
                params.set('q', q)
            }
            params.set('page', page)
            params.set('per', per)

            params.set('orderBy', `${sortDirection}${orderBy.value}`)

            const courseId = selectedCourse?.id ?? selectedCourse?.value
            const editionId = selectedEdition?.id ?? selectedEdition?.value

            if (courseId) {
                params.set('course_id', String(courseId))
            }

            if (editionId) {
                params.set('edition_id', String(editionId))
            }

            const route = `/admin/users?${params.toString()}`

            const students = await api.get(route, { signal: controller.signal })
            if (controller.signal.aborted) return
            const { users, count } = students
            setStudents(users)
            setCount(count)
        } catch (e) {
            if (!controller.signal.aborted) console.error(e)
        } finally {
            if (!controller.signal.aborted) {
                setLoadingStudents(false)
                setLoadingPage(false)
            }
        }
    }, [page, per, orderBy, sortDirection, selectedCourse, selectedEdition])

    useEffect(() => {
        getStudents(q)
        return () => requestRef.current?.abort()
    }, [getStudents, q])

    useEffect(() => {
        getCourses()
    }, [])

    useEffect(() => {
        if (selectedCourse) {
            getEditions(selectedCourse.id)
        }
        else {
            setEditions([])
            setSelectedEdition(null)
        }
    }, [selectedCourse])

    const getCourses = async () => {
        setLoading(prev => ({ ...prev, courses: true }))
        try {
            const courses = await api.get("/admin/courses")
            setCourses(courses.sort((a, b) => a.name.localeCompare(b.name)).map(c => ({ id: c.id, name: c.name, value: c.id })))
            setFilteredCourses(courses.sort((a, b) => a.name.localeCompare(b.name)).map(c => ({ id: c.id, name: c.name, value: c.id })))
        } catch (e) {
            console.error(e)
            return []
        }
        setLoading(prev => ({ ...prev, courses: false }))
    }

    const getEditions = async (courseId) => {
        setLoading(prev => ({ ...prev, editions: true }))
        try {
            const editions = await api.get(`/admin/courses/${courseId}/editions`)
            setEditions(editions.sort((a, b) => new Date(b.expired_at) - new Date(a.expired_at)).map(e => ({ id: e.id, name: e.name, value: e.id })))
        } catch (e) {
            console.error(e)
            return []
        }
        setLoading(prev => ({ ...prev, editions: false }))
    }

    const onSearch = useCallback((text) => {
        const search = text?.trim() || ''
        setPage(0)
        setQ(search.length >= 3 ? search : null)
    }, [])

    const addStudent = async () => {
        setAddStatus(ButtonStatus.Loading)
        try {
            const newUser = {
                name: name.trim(),
                surname: surname.trim(),
                email: email.trim()
            }
            await api.post("/admin/users", newUser)
            setAddStatus(ButtonStatus.Success)
            resetDialog()
            getStudents(q)
        } catch (e) {
            console.error(e)
            setAddStatus(ButtonStatus.Error)
        }
    }

    const resetDialog = () => {
        setOpen(false)
        setName("")
        setSurname("")
        setEmail("")
    }

    const onAddClick = () => setOpen(true)

    useImperativeHandle(ref, () => {
        return {
            onAddClick
        }
    })

    const getStudentStatusBadge = (status) => {
        switch (status) {
            case 'verified':
                return <StatusBadge appearance="soft" label={"VERIFICATO"} color={"var(--sf-green)"} />
            case 'locked':
                return <StatusBadge appearance="soft" label={"BLOCCATO"} color={"var(--sf-gray)"} />
            case 'unverified':
                return <StatusBadge appearance="soft" label={"NON VERIFICATO"} color={"var(--sf-red)"} />
            case 'suspended':
                return <StatusBadge appearance="soft" label={"SOSPESO"} color={"var(--sf-orange)"} />
            default:
                return null
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.section}>
                <div className={styles.sectionInner}>

                    <div className={styles.students}>
                        <div className={styles.searchBar}>
                            <TextInput
                                onKeyUp={(value) => {
                                    onSearch(value)
                                }}
                                type="search" appearance="modern"
                                placeholder={t("search")}
                                style={{
                                    backgroundColor: "var(--background-secondary-color)",

                                }} />
                        </div>
                        <div className={styles.filters}>
                            <Select appearance="modern"
                            disabled={loading.courses}
                            placeholder={loading.courses ? "Caricando corsi..." : "Seleziona Corso"} 
                            nullable 
                            options={filteredCourses} 
                            selected={filteredCourses.find(o => o.value === selectedCourse?.value)}
                            searchPlaceholder={"Cerca corso"}
                            onSearch={(v) => {
                                if (v) {
                                    setFilteredCourses(courses.filter(c => c.name.toLowerCase().includes(v.toLowerCase())).map(c => ({ id: c.id, name: c.name, value: c.id })))
                                } else {
                                    setFilteredCourses([...courses])
                                }
                            }}
                            onSelect={(item) => {
                                const nextCourse = item.id !== -1 ? item : null
                                setSelectedCourse(nextCourse)
                                setSelectedEdition(null)
                                setPage(0)
                            }} />
                            <Select appearance="modern"
                            disabled={loading.editions || !selectedCourse}
                            placeholder={loading.editions ? "Caricando edizioni..." : "Seleziona Edizione"} nullable options={editions} selected={editions.find(o => o.value === selectedEdition?.value)} onSelect={(item) => {
                                setSelectedEdition(item.id !== -1 ? item : null)
                                setPage(0)
                            }} />
                        </div>
                        <Table appearance="minimal"
                            style={{ borderRadius: 0, marginTop: 0 }}
                            columns={[
                                { key: 'id', label: 'ID', type: 'number' },
                                { key: 'name', label: 'NOME', type: 'string' },
                                { key: 'surname', label: 'COGNOME', type: 'string' },
                                { key: 'email', label: 'EMAIL', type: 'string' },
                                { key: 'phone', label: 'TELEFONO', type: 'string' },
                                { key: 'age', label: 'ETÀ', type: 'number' },
                                { key: 'city', label: 'CITTÀ', type: 'string' },
                                { key: 'status', label: 'STATO', type: 'custom' },
                                { key: 'created_at', label: 'CREATO IL', type: 'datetime' },
                                { key: 'registered_at', label: 'REGISTRATO IL', type: 'datetime' },
                                { key: 'last_access', label: 'ULTIMO ACCESSO', type: 'datetime' },
                                { key: 'courses', label: 'CORSI', type: 'custom' },
                            ]}
                            loading={loadingStudents}
                            elements={students.map(s => ({
                                ...s,
                                status: getStudentStatusBadge(s.status),
                                courses: {
                                    value: s.courses.length, type: 'number', component: s.courses.length > 0 ? <TooltipWrapper
                                        component={
                                            <div>
                                                <Each of={s.courses} render={(c) => {
                                                    return (
                                                        <div>
                                                            {c.name}
                                                        </div>
                                                    )
                                                }} />
                                            </div>}>
                                        <span style={{ whiteSpace: 'nowrap' }}>{s.courses.length} Corsi</span>
                                    </TooltipWrapper> : <span>Nessun corso</span>
                                },
                                href: `/users/students/${s.id}`,
                            }))}
                            defaultSort={{ key: 'created_at', direction: 'desc' }}
                        />
                    </div>
                    {
                        <Pagination total={count} per={per} page={page} group={width > 540 ? 5 : 0} onChange={p => setPage(p)} loading={loadingPage} />
                    }
                </div>
            </div>
            <Dialog
                appearance="modern"
                open={open}
                onClose={resetDialog}
                title={`Aggiungi studente`}
                style={{ maxWidth: "512px" }}
            >
                <div className={formStyles.dialog}>
                    <div className={formStyles.inputs}>
                        <TextInput appearance="modern" value={name} type="text" label={capitalize(t('profile.name'))} placeholder={capitalize(t('profile.name'))} onKeyUp={v => setName(v)}></TextInput>
                        <TextInput appearance="modern" value={surname} type="text" label={capitalize(t('profile.surname'))} placeholder={capitalize(t('profile.surname'))} onKeyUp={v => setSurname(v)}></TextInput>
                        <TextInput appearance="modern" value={email} type="text" label={capitalize(t('profile.email'))} placeholder={capitalize(t('profile.email'))} onKeyUp={v => setEmail(v)} />
                        <div className={formStyles.bottom}>
                            <Button
                                style={{ padding: '.65rem 1rem', borderRadius: 8, fontSize: '.75rem' }}
                                accentColor={"var(--primary)"}
                                disabled={!name || !surname || !email || addStatus === DialogStatus.Loading || addStatus === DialogStatus.Success}
                                status={addStatus}
                                onClick={addStudent}
                            >
                                Aggiungi
                                {
                                    addStatus === null &&
                                    <AddIcon />
                                }
                            </Button>
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    )
})

export default Students
